// Figma frame 02 link sent (33:915). US-081.
//
// `email` is optional on purpose: this screen must survive being opened
// directly (a bookmark, a reload, a link shared in Slack) with no prior
// navigation state. Without an address we cannot resend to it, so the
// resend action is simply not offered and the copy stays generic — never a
// crash, never an invented address.

import { useEffect, useState } from "react";
import { Button, Icon, Toast } from "../../ui";
import { AuthShell } from "./AuthShell";
import { requestMagicLink } from "./authApi";

const RESEND_COOLDOWN_S = 60;

/** Ticks a countdown to 0 and stays there; `restart` resets it. */
function useCountdown(initialSeconds: number) {
  const [seconds, setSeconds] = useState(initialSeconds);
  useEffect(() => {
    if (seconds <= 0) return;
    const id = window.setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [seconds]);
  return [seconds, setSeconds] as const;
}

export type LinkSentScreenProps = {
  email?: string;
  onUseDifferentEmail: () => void;
};

type ResendState = "idle" | "sending" | "sent" | "error";

export function LinkSentScreen({ email, onUseDifferentEmail }: LinkSentScreenProps) {
  const [cooldown, setCooldown] = useCountdown(RESEND_COOLDOWN_S);
  const [resendState, setResendState] = useState<ResendState>("idle");

  async function handleResend() {
    if (!email || cooldown > 0 || resendState === "sending") return;
    setResendState("sending");
    try {
      await requestMagicLink(email);
      setResendState("sent");
      setCooldown(RESEND_COOLDOWN_S);
    } catch {
      setResendState("error");
    }
  }

  return (
    <AuthShell>
      <span className="maisight-signin-icon" aria-hidden="true">
        <Icon name="mail" size={28} />
      </span>
      <h1 className="maisight-signin-heading type-h2">Check your email</h1>
      <p className="maisight-signin-body type-bodyS">
        {email ? (
          <>
            We sent a sign-in link to <strong>{email}</strong>. It expires in
            15 minutes.
          </>
        ) : (
          "We sent a sign-in link to your email address. It expires in 15 minutes."
        )}
      </p>
      <div className="maisight-signin-link-sent-form">
        <Button variant="secondary" block onClick={onUseDifferentEmail}>
          Use a different email
        </Button>
        {email ? (
          <p className="maisight-signin-resend type-bodyS">
            No email? Check spam, or{" "}
            {cooldown > 0 ? (
              <>wait {cooldown}s and try again.</>
            ) : (
              <button
                type="button"
                className="maisight-signin-resend-action"
                onClick={() => void handleResend()}
                disabled={resendState === "sending"}
              >
                try again
              </button>
            )}
          </p>
        ) : (
          <p className="maisight-signin-resend type-bodyS">
            No email? Check spam, or use a different email above.
          </p>
        )}
      </div>
      {resendState === "sent" && email ? (
        <Toast
          title="Link resent"
          description={`We sent another link to ${email}.`}
          tone="nominal"
          onDismiss={() => setResendState("idle")}
        />
      ) : null}
      {resendState === "error" ? (
        <Toast
          title="Couldn't resend"
          description="Check your connection and try again."
          tone="danger"
          onDismiss={() => setResendState("idle")}
        />
      ) : null}
    </AuthShell>
  );
}
